import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronRight, HardHat, MapPin, CheckSquare, Quote, Info } from "lucide-react";
import { PortalPage, Card, Spinner, ErrorNote } from "@/components/PortalPage";
import { Bullets, Inline } from "@/components/ContentParts";
import { api } from "@/lib/api";

const SectionCard = ({ s, mine, onOpen }) => (
  <button onClick={onOpen} data-testid={`playbook-section-${s.slug.replace("/", "-")}`}
    className={`flex w-full items-center gap-4 rounded-2xl border bg-[#1A1A1A] p-4 text-left transition-colors hover:border-[var(--accent)] ${mine ? "border-[var(--accent)]" : "border-[#333]"}`}>
    <span className="flex-1">
      {mine && <span className="mb-1 inline-flex items-center gap-1 rounded-full bg-[var(--accent)] px-2 py-0.5 text-[9px] font-extrabold tracking-wider text-black"><MapPin size={10} /> YOU ARE HERE</span>}
      {s.days_label && <span className="block text-[10px] font-bold tracking-[0.2em] text-[var(--accent)]">{s.days_label}</span>}
      <span className="block font-semibold">{s.title}</span>
      {s.subtitle && <span className="block text-xs text-neutral-400">{s.subtitle}</span>}
    </span>
    <ChevronRight size={18} className="text-neutral-500" />
  </button>
);

export function PlaybookIndex() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    Promise.all([api("/playbook/sections"), api("/playbook/section-for-me").catch(() => ({ slug: "" }))])
      .then(([s, mine]) => setData({ sections: s.sections || [], mine: mine.slug }))
      .catch((e) => setErr(e.message));
  }, []);
  const group = (pb) => data.sections.filter((s) => s.playbook === pb);
  const H = ({ children }) => <h2 className="pt-4 text-[11px] font-bold tracking-[0.3em] text-neutral-400">{children}</h2>;

  return (
    <PortalPage eyebrow="ALR PLAYBOOK" title="How ALR franchisees grow" testId="playbook-page">
      <p className="text-sm text-neutral-400">How every ALR franchisee grows — from day one to a business that runs without you.</p>
      <ErrorNote>{err}</ErrorNote>
      {!data && !err && <Spinner />}
      {data && (
        <>
          <H>FOR FIELD OPERATORS & EMPLOYEES</H>
          <button onClick={() => navigate("/employee-success-guide")} data-testid="playbook-field-operator" className="flex w-full items-center gap-4 rounded-2xl border border-[#333] bg-[#1A1A1A] p-4 text-left hover:border-[var(--accent)]">
            <HardHat size={22} className="text-[var(--accent)]" />
            <span className="flex-1"><span className="block font-semibold">Field Operator's Playbook</span><span className="text-xs text-neutral-400">How ALR techs show up, work safely, and deliver every job to brand standard.</span></span>
            <ChevronRight size={18} className="text-neutral-500" />
          </button>
          <H>FIRST 120 DAYS</H>
          {group("first_120").map((s) => <SectionCard key={s.slug} s={s} mine={data.mine === s.slug} onOpen={() => navigate(`/playbook/${s.slug}`)} />)}
          <H>LEVEL UP</H>
          {group("level_up").map((s) => <SectionCard key={s.slug} s={s} mine={data.mine === s.slug} onOpen={() => navigate(`/playbook/${s.slug}`)} />)}
        </>
      )}
    </PortalPage>
  );
}

const TONES = { info: "#3B82F6", green: "var(--accent)", amber: "#F5A623", red: "#FF3333" };

const Block = ({ b }) => {
  if (b.type === "heading") return <h2 className="pt-4 font-display text-2xl font-bold text-[var(--accent)]">{b.text}</h2>;
  if (b.type === "paragraph") return <p className="text-[15px] leading-7 text-neutral-200"><Inline text={b.text} /></p>;
  if (b.type === "bullet_list") return <div className="text-[15px] leading-7 text-neutral-200"><Bullets items={b.items} /></div>;
  if (b.type === "numbered_list") return <ol className="list-decimal space-y-1.5 pl-6 text-[15px] leading-7 text-neutral-200 marker:font-bold marker:text-[var(--accent)]">{b.items.map((x, i) => <li key={i}><Inline text={x} /></li>)}</ol>;
  if (b.type === "quote") return <blockquote className="flex gap-3 border-l-4 border-[var(--accent)] py-1 pl-4 text-lg italic text-white"><Quote size={18} className="shrink-0 text-[var(--accent)]" />{b.text}</blockquote>;
  if (b.type === "callout") return <div className="flex gap-3 rounded-xl border p-4 text-sm" style={{ borderColor: TONES[b.tone] || TONES.info }}><Info size={18} className="shrink-0" style={{ color: TONES[b.tone] || TONES.info }} /><span><Inline text={b.text} /></span></div>;
  if (b.type === "checklist") return <ul className="space-y-2">{b.items.map((x, i) => <li key={i} className="flex gap-3 rounded-lg border border-[#333] bg-[#1A1A1A] p-3 text-sm"><CheckSquare size={18} className="shrink-0 text-[var(--accent)]" /><span><Inline text={x} /></span></li>)}</ul>;
  return null;
};

export function PlaybookSection() {
  const { playbook, phase } = useParams();
  const [s, setS] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => { setS(null); api(`/playbook/section/${playbook}/${phase}`).then(setS).catch((e) => setErr(e.message)); }, [playbook, phase]);
  return (
    <PortalPage eyebrow={s?.days_label || "ALR PLAYBOOK"} title={s?.title || "Playbook"} back="/playbook" backLabel="Playbook" testId="playbook-section-page">
      <ErrorNote>{err}</ErrorNote>
      {!s && !err && <Spinner />}
      {s && (
        <Card className="space-y-4 p-6" data-testid="playbook-section-body">
          {s.subtitle && <p className="text-sm text-neutral-400">{s.subtitle}</p>}
          {(s.body || []).map((b, i) => <Block key={i} b={b} />)}
        </Card>
      )}
    </PortalPage>
  );
}
